/**
 * @fileoverview Order by dropdown select
 */

import { memo, useMemo } from 'react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Group, Text, Select } from 'components'
import { ORDER_BY_OPTIONS } from '../types'
import type { OrderByOption, SortDirection } from '../types'

interface OrderBySelectProps {
  value: OrderByOption
  direction: SortDirection
  onChange: (value: OrderByOption, direction: SortDirection) => void
}

/**
 * Dropdown select for ordering projects.
 * Options: Last updated, Name, Status, Created at
 */
export const OrderBySelect: FC<OrderBySelectProps> = memo(({
  value,
  direction,
  onChange,
}) => {
  const { t } = useTranslation()

  const data = useMemo(
    () => ORDER_BY_OPTIONS.map(option => ({
      value: option.value,
      label: t(option.label),
    })),
    [t],
  )

  const handleChange = (newValue: string | null) => {
    if (!newValue) return

    const option = ORDER_BY_OPTIONS.find(opt => opt.value === newValue)
    const newDirection = option?.defaultDirection ?? 'desc'

    onChange(newValue as OrderByOption, newDirection)
  }

  return (
    <Group spacing="sm" noWrap>
      <Text size="sm" weight={600} color="dimmed" style={{ whiteSpace: 'nowrap' }}>
        {t('arc_projects.order_by')}
      </Text>
      <Select
        value={value}
        onChange={handleChange}
        data={data}
        size="sm"
        aria-label={t('arc_projects.order_by_aria')}
        styles={{
          input: {
            minWidth: 140,
          },
        }}
      />
    </Group>
  )
})

OrderBySelect.displayName = 'OrderBySelect'
